/**
 * Die Schubladen der Seitenleiste: „Arbeiten“, „Wissen“, „Mehr“. (22.10.2026)
 *
 * Martin: *„Bitte auch die Sidebar komplett überarbeiten … sie ist komplett überladen.“* Täglich bleibt offen,
 * der Rest liegt in `<details>` — zu, bis man sie öffnet. Dieses Modul tut zweierlei:
 *   - es MERKT je Gerät, welche Schublade offen ist (wer „Arbeiten“ täglich braucht, lässt sie offen);
 *   - steht man auf einer Seite, deren Eintrag in einer zugeklappten Schublade liegt (`aria-selected`), geht die
 *     Schublade auf — sonst wäre „du bist hier“ unsichtbar. Das wird NICHT gemerkt: es ist ein Blick, kein Wunsch.
 *
 *   - in der SCHMALEN Symbolleiste (body.nav-rail) gibt es keine Köpfe zum Aufklappen: dort öffnet es Arbeiten und
 *     Wissen (ohne zu merken), damit ihre Zeichen zu sehen sind; zurück in der vollen Leiste gilt wieder das Gemerkte.
 *     Das Blatt tut dasselbe über `::details-content` — diese Regel kennen ältere Safari (< 18.4) und Firefox
 *     (< 143) nicht; das Skript ist dort der Weg. (Prüfer-Auflage 22.10.2026)
 *
 * ⚠️ Der Speicher ist nur Bequemlichkeit: gesperrt (privater Modus) gilt die Vorgabe aus dem Markup (zu).
 * ⚠️ Auf- und Zuklappen selbst macht der Browser (`<details>`/`<summary>`: Tastatur, Vorleser, aufgeklappt-Zustand).
 */

const SCHLUESSEL = "vespera.nav-gruppen";

type Stand = Record<string, boolean>;

function lies(): Stand {
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    const d: unknown = roh ? JSON.parse(roh) : null;
    if (!d || typeof d !== "object" || Array.isArray(d)) return {};
    const aus: Stand = {};
    for (const [k, v] of Object.entries(d as Record<string, unknown>)) if (typeof v === "boolean") aus[k] = v;
    return aus;
  } catch {
    return {};
  }
}

function schreib(s: Stand): void {
  try {
    localStorage.setItem(SCHLUESSEL, JSON.stringify(s));
  } catch {
    /* gilt dann bis zum Neuladen */
  }
}

/** Setzt `open`, ohne dass der Wechsel als Wunsch gemerkt wird. Nur bei echtem Wechsel (sonst käme kein „toggle“). */
function setzeVonSelbst(d: HTMLDetailsElement, auf: boolean): void {
  if (d.open === auf) return;
  d.dataset.vonSelbst = "1";
  d.open = auf;
}

/**
 * Symbolleiste an: Arbeiten und Wissen auf (Mehr bleibt, wie es war — es hat in der Symbolleiste keinen Platz).
 * Symbolleiste aus: der gemerkte Stand (Vorgabe zu), dann „du bist hier“.
 */
export function stelleFuerSymbolleiste(liste: ParentNode, schmal: boolean): void {
  const stand = lies();
  for (const d of liste.querySelectorAll<HTMLDetailsElement>("details.nav-gruppe[id]")) {
    setzeVonSelbst(d, schmal ? true : stand[d.id] === true);
  }
  if (!schmal) oeffneSchubladeMitOrt(liste);
}

/** Öffnet jede zugeklappte Schublade, in der der gewählte Eintrag steht. Gibt zurück, ob etwas aufging. */
export function oeffneSchubladeMitOrt(liste: ParentNode): boolean {
  let auf = false;
  for (const d of liste.querySelectorAll<HTMLDetailsElement>("details[id]")) {
    if (d.open) continue;
    if (d.querySelector('[aria-selected="true"], [aria-current="page"]')) {
      setzeVonSelbst(d, true);
      auf = true;
    }
  }
  return auf;
}

/**
 * Bindet die Schubladen einer Liste: gemerkten Stand anwenden, Wechsel merken, bei Ortswechsel die passende öffnen.
 * Gibt eine Abmeldung zurück (für Tests).
 */
export function bindeNavGruppen(liste: HTMLElement, win: Window = window): () => void {
  const schubladen = [...liste.querySelectorAll<HTMLDetailsElement>("details[id]")];
  const stand = lies();
  for (const d of schubladen) {
    if (stand[d.id] !== undefined) d.open = stand[d.id]!;
  }
  const merke = (ev: Event): void => {
    const d = ev.target as HTMLDetailsElement;
    if (d.dataset.vonSelbst) {
      delete d.dataset.vonSelbst;
      return;
    }
    const s = lies();
    s[d.id] = d.open;
    schreib(s);
  };
  for (const d of schubladen) d.addEventListener("toggle", merke);
  const ort = (): void => {
    win.setTimeout(() => oeffneSchubladeMitOrt(liste), 0);
  };
  win.addEventListener("voice-v8:nav-changed", ort);
  win.addEventListener("hashchange", ort);
  ort();
  // Symbolleiste: app.ts (applyNavMode) schaltet body.nav-rail — hier wird nur gelesen.
  const body = win.document.body;
  let schmal = body.classList.contains("nav-rail");
  if (schmal) stelleFuerSymbolleiste(liste, true);
  const beobachter = new MutationObserver(() => {
    const jetzt = body.classList.contains("nav-rail");
    if (jetzt === schmal) return;
    schmal = jetzt;
    stelleFuerSymbolleiste(liste, jetzt);
  });
  beobachter.observe(body, { attributes: true, attributeFilter: ["class"] });
  return () => {
    beobachter.disconnect();
    for (const d of schubladen) d.removeEventListener("toggle", merke);
    win.removeEventListener("voice-v8:nav-changed", ort);
    win.removeEventListener("hashchange", ort);
  };
}
